import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

interface TestCaseResult {
  readonly testCaseId: string;
  readonly testStatus: string;
}

export function M24RuleTestingSandboxPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [ruleId, setRuleId] = useState('');
  const [ruleLabel, setRuleLabel] = useState('');
  const [testName, setTestName] = useState('');
  const [testData, setTestData] = useState('{\n  "amount": 500\n}');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(testData);
    } catch {
      setError(t('m24.sandbox.invalidJson'));
      return;
    }
    setSubmitting(true);
    apiRequest<{ data: TestCaseResult }>('/v1/rule-test-cases', {
      method: 'POST',
      token,
      body: { ruleId, testName, testData: parsed },
    })
      .then((created) =>
        apiRequest<{ data: TestCaseResult }>(`/v1/rule-test-cases/${created.data.testCaseId}/run`, {
          method: 'POST',
          token,
        }),
      )
      .then((run) => { setMessage(t('m24.sandbox.msgResult', { status: run.data.testStatus, rule: ruleLabel })); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="testing-sandbox">
      <h1>{t('m24.sandbox.title')}</h1>
      <form aria-label="test-form" onSubmit={onSubmit}>
        <RulePicker label={t('m24.sandbox.ruleId')} value={ruleId} onPick={(id, label) => { setRuleId(id); setRuleLabel(label); }} />
        <label htmlFor="testName">{t('m24.sandbox.testName')}</label>
        <input id="testName" value={testName} onChange={(e) => setTestName(e.target.value)} />
        <label htmlFor="testData">{t('m24.sandbox.testData')}</label>
        <textarea id="testData" value={testData} onChange={(e) => setTestData(e.target.value)} rows={6} />
        <button type="submit" disabled={submitting || !ruleId.trim() || !testName.trim() || !testData.trim()}>{t('m24.sandbox.submit')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
